import React, { useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Input from '../../components/ui/Input';
import { imagePath } from '../../utils/imagePath';
import { HelpCenterData } from '../../components/data/data/HelpCenter/HelpCenterData';
import { ARTICLE_DATA } from '../../components/data/data/HelpCenter/ArticlesData';
import { generateSectionId } from '../../components/HelpCenterComponents/ArticleLayout';
import useClickOutside from '../../components/UseClickOutside';
import { documentationData } from '../data/data/Documentation/documentationData';

const MIN_CHARS = 2;

const UnifiedSearchBar = ({
  placeholder = 'Search the Spatial Grid Help Center',
  className = '',
  showResultsCount = true,
  variant = 'full',
  onSearchChange = null,
  onSearchSubmit = null, // ✅ new: called with searchResults when Enter is pressed
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const searchBoxRef = useRef(null);

  useClickOutside(searchBoxRef, () => {
    setDropdownOpen(false);
  });

  const searchResults = useMemo(() => {
    if (searchQuery.trim().length < MIN_CHARS) return null;

const query = searchQuery.toLowerCase();
const queryWords = query.trim().split(/\s+/);

const matchesQuery = (text) =>
  queryWords.every((word) => text?.toLowerCase().includes(word));    const results = {
      categories: [],
      articles: [],
      sections: [],
      documentation: [],
    };

    // Level 1: Search in main categories
    HelpCenterData.forEach((item) => {
      if (matchesQuery(item?.title) || matchesQuery(item?.desc)) {
        results.categories.push({
          type: 'category',
          title: item.title,
          description: item.desc,
          path: item.path,
        });
      }
    });


    

    // Level 2 & 3: Search in articles and sections
    Object.entries(ARTICLE_DATA).forEach(([categoryKey, categoryData]) => {
      categoryData.titles.forEach((articleTitle, articleIndex) => {
        if (matchesQuery(articleTitle)) {
          results.articles.push({
            type: 'article',
            title: articleTitle,
            categoryName: categoryData.categoryName,
            path: `${categoryData.basePath}?article=${articleIndex}`,
          });
        }

        const sections = categoryData.tocData[articleIndex] || [];
        sections.forEach((sectionTitle) => {
          if (matchesQuery(sectionTitle)) {
            results.sections.push({
              type: 'section',
              title: sectionTitle,
              articleTitle: articleTitle,
              categoryName: categoryData.categoryName,
              path: `${categoryData.basePath}?article=${articleIndex}#${generateSectionId(sectionTitle)}`,
            });
          }
        });
      });
    });

    // Documentation search
    documentationData.forEach((section) => {
      if (matchesQuery(section.sectionTitle)) {
        results.documentation.push({
          type: 'documentation',
          title: section.sectionTitle,
          path: `/documentation/${section.slug}`,
        });
      }

      section.subSections?.forEach((sub) => {
        if (matchesQuery(sub.title)) {
          results.documentation.push({
            type: 'documentation',
            title: `${section.sectionTitle} → ${sub.title}`,
            path: `/documentation/${section.slug}`,
            anchor: sub.title.toLowerCase().replace(/\s+/g, '-'),
          });
        }
      });
    });

    return results;
  }, [searchQuery]);

  const totalResults = searchResults
    ? searchResults.categories.length +
      searchResults.articles.length +
      searchResults.sections.length +
      searchResults.documentation.length
    : 0;

  const handleSearchChange = (e) => {
    const newQuery = e.target.value;
    setSearchQuery(newQuery);
    setDropdownOpen(newQuery.trim().length >= MIN_CHARS);
    if (onSearchChange) onSearchChange(newQuery);
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setDropdownOpen(false);
    if (onSearchChange) onSearchChange('');
    if (onSearchSubmit) onSearchSubmit(null); // clear inline results too
  };

  // ✅ Enter key: push results inline and close dropdown
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && searchResults) {
      setDropdownOpen(false);
      if (onSearchSubmit)
        onSearchSubmit({ query: searchQuery, results: searchResults });
    }
  };

  // ✅ Clicking a dropdown result: navigate directly
  const handleResultClick = (result) => {
    if (result.anchor) {
      navigate(result.path, { state: { scrollTo: result.anchor } });
    } else {
      navigate(result.path);
    }
    setSearchQuery('');
    setDropdownOpen(false);
    if (onSearchChange) onSearchChange('');
    if (onSearchSubmit) onSearchSubmit(null);
  };

  const getContainerClass = () => {
    switch (variant) {
      case 'compact':
        return 'w-full md:w-[450px]';
      case 'medium':
        return 'w-full max-w-[650px]';
      case 'full':
      default:
        return 'w-full max-w-[950px]';
    }
  };

  const getInputHeight = () => {
    switch (variant) {
      case 'compact':
        return '!h-[50px]';
      case 'medium':
        return '!h-[48px]';
      case 'full':
      default:
        return '!h-[44px]';
    }
  };

  const getWrapperClass = () => {
    switch (variant) {
      case 'compact':
        return 'flex justify-end';
      default:
        return 'flex justify-center';
    }
  };

  return (
    <div className={`w-full ${className}`}>
      <div className={`${getWrapperClass()} px-4`}>
        <div ref={searchBoxRef} className={`${getContainerClass()} relative`}>
          <div onKeyDown={handleKeyDown}>
            <Input
              leftIcon={
                <img
                  className="absolute left-3 md:left-6 top-1/2 transform -translate-y-1/2 h-[18px]"
                  src={imagePath?.SearchIcon}
                  alt="Search"
                />
              }
              className={`text-base md:text-lg w-full ${getInputHeight()} !rounded-full bg-[#31333A] placeholder:font-light border-none outline-none pl-10 md:pl-20 text-white placeholder:text-[#C1C1C1]`}
              type="text"
              placeholder={placeholder}
              value={searchQuery}
              onChange={handleSearchChange}
              // onKeyDown={handleKeyDown}
            />
          </div>
          {searchQuery && (
            <button
              onClick={handleSearchClear}
              className="absolute right-7 md:right-10 top-1/2 transform -translate-y-1/2 text-[#C1C1C1] hover:text-white transition-colors"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}

          {/* Dropdown — only when open and results exist */}
          {dropdownOpen && searchResults && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-[#31333A] border border-[#5c5c5c] rounded-lg shadow-xl max-h-[400px] overflow-y-auto z-50 scrollbar">
              {totalResults > 0 ? (
                <div className="py-2">
                  {searchResults.categories.length > 0 && (
                    <SearchSection
                      title="Categories"
                      results={searchResults.categories}
                      onResultClick={handleResultClick}
                    />
                  )}
                  {searchResults.articles.length > 0 && (
                    <SearchSection
                      title="Articles"
                      results={searchResults.articles}
                      onResultClick={handleResultClick}
                    />
                  )}
                  {searchResults.sections.length > 0 && (
                    <SearchSection
                      title="Sections"
                      results={searchResults.sections}
                      onResultClick={handleResultClick}
                    />
                  )}
                  {searchResults.documentation.length > 0 && (
                    <SearchSection
                      title="Documentation"
                      results={searchResults.documentation}
                      onResultClick={handleResultClick}
                    />
                  )}
                </div>
              ) : (
                <div className="px-4 py-6 text-center text-sm text-gray-400">
                  No results found for "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Results count — shown only when dropdown is closed (i.e. after Enter) */}
      {showResultsCount && searchQuery && searchResults && !dropdownOpen && (
        <div className="text-center mt-4 text-[#C1C1C1]">
          {totalResults > 0 ? (
            <p>
              Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "
              {searchQuery}"
            </p>
          ) : (
            <p>No results found for "{searchQuery}". Try different keywords.</p>
          )}
        </div>
      )}
    </div>
  );
};

const SearchSection = ({ title, results, onResultClick }) => (
  <div className="border-b border-[#444447] last:border-b-0">
    <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
      {title}
    </div>
    {results.map((result, index) => (
      <div
        key={index}
        onClick={() => onResultClick(result)}
        className="px-4 py-3 hover:bg-[#3a3c43] cursor-pointer transition-colors"
      >
        <div className="flex flex-col gap-1">
          <div className="text-sm font-medium text-white">{result.title}</div>
          {result.description && (
            <div className="text-xs text-gray-400">{result.description}</div>
          )}
          {result.articleTitle && (
            <div className="text-xs text-gray-400">
              in {result.articleTitle}
            </div>
          )}
          {result.categoryName && (
            <div className="text-xs text-[#EE2B2A]">{result.categoryName}</div>
          )}
          {/* {result.type === 'documentation' && (
            <div className="text-xs text-[#519FFF]">📄 Documentation</div>
          )} */}
        </div>
      </div>
    ))}
  </div>
);

export default UnifiedSearchBar;
// import React, { useState, useMemo, useRef } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { imagePath } from '../../utils/imagePath';
// import { HelpCenterData } from '../../components/data/data/HelpCenter/HelpCenterData';
// import { ARTICLE_DATA } from '../../components/data/data/HelpCenter/ArticlesData';
// import { generateSectionId } from '../../components/HelpCenterComponents/ArticleLayout';
// import useClickOutside from '../../components/UseClickOutside';

// /**
//  * UnifiedSearchBar - Reusable search component for Help Center
//  * Supports different sizes: "full", "compact", "medium"
//  * Press Enter to navigate to first result
//  */
// const UnifiedSearchBar = ({
//   placeholder = 'Search the Spatial Grid Help Center',
//   className = '',
//   showResultsCount = true,
//   variant = 'full', // "full" | "compact" | "medium"
//   onSearchChange = null, // Optional callback when search query changes
// }) => {
//   const navigate = useNavigate();
//   const [searchQuery, setSearchQuery] = useState('');
//   const searchBoxRef = useRef(null);

//   useClickOutside(searchBoxRef, () => {
//     setSearchQuery('');
//   });

//   // Search across all levels
//   const searchResults = useMemo(() => {
//     if (!searchQuery.trim()) return null;

//     const query = searchQuery.toLowerCase();
//     const results = {
//       categories: [], // Level 1
//       articles: [], // Level 2 - article titles
//       sections: [], // Level 3 - article sections (TOC)
//     };

//     // Level 1: Search in main categories (HelpCenterData)
//     HelpCenterData.forEach((item) => {
//       if (
//         item?.title?.toLowerCase().includes(query) ||
//         item?.desc?.toLowerCase().includes(query)
//       ) {
//         results.categories.push({
//           type: 'category',
//           title: item.title,
//           description: item.desc,
//           path: item.path,
//         });
//       }
//     });

//     // Level 2 & 3: Search in articles and TOC sections
//     Object.entries(ARTICLE_DATA).forEach(([categoryKey, categoryData]) => {
//       // Level 2: Search in article titles
//       categoryData.titles.forEach((articleTitle, articleIndex) => {
//         if (articleTitle.toLowerCase().includes(query)) {
//           results.articles.push({
//             type: 'article',
//             title: articleTitle,
//             categoryName: categoryData.categoryName,
//             path: `${categoryData.basePath}?article=${articleIndex}`,
//           });
//         }

//         // Level 3: Search in TOC sections
//         const sections = categoryData.tocData[articleIndex] || [];
//         sections.forEach((sectionTitle) => {
//           if (sectionTitle.toLowerCase().includes(query)) {
//             results.sections.push({
//               type: 'section',
//               title: sectionTitle,
//               articleTitle: articleTitle,
//               categoryName: categoryData.categoryName,
//               path: `${categoryData.basePath}?article=${articleIndex}#${generateSectionId(sectionTitle)}`,
//             });
//           }
//         });
//       });
//     });

//     return results;
//   }, [searchQuery]);

//   // Calculate total results
//   const totalResults = searchResults
//     ? searchResults.categories.length +
//       searchResults.articles.length +
//       searchResults.sections.length
//     : 0;

//   // Get first result for Enter key navigation
//   const getFirstResult = () => {
//     if (!searchResults || totalResults === 0) return null;

//     // Priority: Categories > Articles > Sections
//     if (searchResults.categories.length > 0) return searchResults.categories[0];
//     if (searchResults.articles.length > 0) return searchResults.articles[0];
//     if (searchResults.sections.length > 0) return searchResults.sections[0];

//     return null;
//   };

//   const handleSearchChange = (e) => {
//     const newQuery = e.target.value;
//     setSearchQuery(newQuery);
//     if (onSearchChange) {
//       onSearchChange(newQuery);
//     }
//   };

//   const handleSearchClear = () => {
//     setSearchQuery('');
//     if (onSearchChange) {
//       onSearchChange('');
//     }
//   };

//   const handleResultClick = (path) => {
//     navigate(path);
//     setSearchQuery('');
//   };

//   // Handle Enter key press
//   const handleKeyDown = (e) => {
//     console.log('Key pressed:', e.key); // Debug log

//     if (e.key === 'Enter') {
//       e.preventDefault(); // Prevent form submission
//       const firstResult = getFirstResult();
//       console.log('First result:', firstResult); // Debug log

//       if (firstResult) {
//         console.log('Navigating to:', firstResult.path); // Debug log
//         handleResultClick(firstResult.path);
//       } else {
//         console.log('No results to navigate to'); // Debug log
//       }
//     }
//     // Handle Escape key
//     if (e.key === 'Escape') {
//       setSearchQuery('');
//     }
//   };

//   // Variant-based styling
//   const getContainerClass = () => {
//     switch (variant) {
//       case 'compact':
//         return 'w-full md:w-[450px]';
//       case 'medium':
//         return 'w-full max-w-[650px]';
//       case 'full':
//       default:
//         return 'w-full max-w-[950px]';
//     }
//   };

//   const getInputHeight = () => {
//     switch (variant) {
//       case 'compact':
//         return '!h-[50px]';
//       case 'medium':
//         return '!h-[48px]';
//       case 'full':
//       default:
//         return '!h-[44px]';
//     }
//   };

//   const getWrapperClass = () => {
//     switch (variant) {
//       case 'compact':
//         return 'flex justify-end';
//       default:
//         return 'flex justify-center';
//     }
//   };

//   return (
//     <div className={`w-full ${className}`}>
//       <div className={`${getWrapperClass()} px-4`}>
//         <div ref={searchBoxRef} className={`${getContainerClass()} relative`}>
//           <div className="relative w-full">
//             <img
//               className="absolute left-3 md:left-6 top-1/2 transform -translate-y-1/2 h-[18px] z-10"
//               src={imagePath?.SearchIcon}
//               alt="Search"
//             />
//             <input
//               className={`text-base md:text-lg w-full ${getInputHeight()} !rounded-full bg-[#31333A] placeholder:font-light border-none outline-none pl-10 md:pl-20 pr-16 text-white placeholder:text-[#C1C1C1]`}
//               type="text"
//               placeholder={placeholder}
//               value={searchQuery}
//               onChange={handleSearchChange}
//               onKeyDown={handleKeyDown}
//             />
//           </div>
//           {searchQuery && (
//             <button
//               onClick={handleSearchClear}
//               className="absolute right-7 md:right-10 top-1/2 transform -translate-y-1/2 text-[#C1C1C1] hover:text-white transition-colors"
//               aria-label="Clear search"
//             >
//               ✕
//             </button>
//           )}

//           {/* Search Results Dropdown */}
//           {searchQuery && searchResults && (
//             <div className="absolute top-full left-0 right-0 mt-2 bg-[#31333A] border border-[#5c5c5c] rounded-lg shadow-xl max-h-[400px] overflow-y-auto z-50 scrollbar">
//               {totalResults > 0 ? (
//                 <div className="py-2">
//                   {/* Press Enter hint */}
//                   <div className="px-4 py-2 text-xs text-gray-400 border-b border-[#444447] flex items-center gap-2">
//                     Press{' '}
//                     <kbd className="px-2 py-1 bg-[#444447] rounded text-white text-[10px]">
//                       Enter ↵
//                     </kbd>{' '}
//                     to navigate to first result
//                   </div>

//                   {/* Categories */}
//                   {searchResults.categories.length > 0 && (
//                     <SearchSection
//                       title="Categories"
//                       results={searchResults.categories}
//                       onResultClick={handleResultClick}
//                     />
//                   )}

//                   {/* Articles */}
//                   {searchResults.articles.length > 0 && (
//                     <SearchSection
//                       title="Articles"
//                       results={searchResults.articles}
//                       onResultClick={handleResultClick}
//                     />
//                   )}

//                   {/* Sections */}
//                   {searchResults.sections.length > 0 && (
//                     <SearchSection
//                       title="Sections"
//                       results={searchResults.sections}
//                       onResultClick={handleResultClick}
//                     />
//                   )}
//                 </div>
//               ) : (
//                 <div className="px-4 py-6 text-center text-sm text-gray-400">
//                   No results found for "{searchQuery}"
//                 </div>
//               )}
//             </div>
//           )}
//         </div>
//       </div>

//       {/* Search Results Count */}
//       {showResultsCount && searchQuery && searchResults && (
//         <div className="text-center mt-4 text-[#C1C1C1]">
//           {totalResults > 0 ? (
//             <p>
//               Found {totalResults} result{totalResults !== 1 ? 's' : ''} for "
//               {searchQuery}"
//             </p>
//           ) : (
//             <p>No results found for "{searchQuery}". Try different keywords.</p>
//           )}
//         </div>
//       )}
//     </div>
//   );
// };

// /**
//  * Search Section Component
//  */
// const SearchSection = ({ title, results, onResultClick }) => (
//   <div className="border-b border-[#444447] last:border-b-0">
//     <div className="px-4 py-2 text-xs font-semibold text-gray-400 uppercase">
//       {title}
//     </div>
//     {results.map((result, index) => (
//       <div
//         key={index}
//         onClick={() => onResultClick(result.path)}
//         className="px-4 py-3 hover:bg-[#3a3c43] cursor-pointer transition-colors"
//       >
//         <div className="flex flex-col gap-1">
//           <div className="text-sm font-medium text-white">{result.title}</div>
//           {result.description && (
//             <div className="text-xs text-gray-400">{result.description}</div>
//           )}
//           {result.articleTitle && (
//             <div className="text-xs text-gray-400">
//               in {result.articleTitle}
//             </div>
//           )}
//           {result.categoryName && (
//             <div className="text-xs text-[#EE2B2A]">{result.categoryName}</div>
//           )}
//         </div>
//       </div>
//     ))}
//   </div>
// );

// export default UnifiedSearchBar;
